import React from "react"

// every internal link starts with $url. and closed with $
// every internal link has to contain at least one word: $url.parameter$
// an internal link might contain a second word: $url.workspace.create$
// an internal link might contain one or more number after one or more words:
// $url.item.456$
// $url.report.execute.456$
// $url.smartlist.456.123$
// $url.actionbutton.concept.456.123$
// an internal link can contain a label, which will become the name text content of the link
// $url.item.456#This is the content for the link$

const externalLinksRegex =
    /\b((?:(?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$]))\b/gi

const localFilePathRegex =
    /\b\p{Letter}:[\\](?:[^\\/:*?"<>|\r\n\s]+[\\])*[^\\/:*?"<>|\r\n\s]*\b/gu

const UNCFilePathRegex =
    /\\\\(?:\?\\UNC\\)?[\p{Letter}\p{Number}._%~-]+\\[\p{Letter}\p{Number}._%~-]+(?:\\[\p{Letter}\p{Number}._%~-]+)*/gu

// for this weird regex, the actual regex is /\$url(?:\.\w+)+(?:#([^\s$\p{Separator}\p{Control}][^$]*))/gu look up lookarounds in regexes, we have it here to make sure there are no [a-zA-z0-9_] characters right before or behind the url
const internalLinksRegex =
    /(?<!\w)\$url(?:\.\w+)+(?:#([^\s$\p{Separator}\p{Control}][^$]*))?\$(?!\w)/gu

export const urlify = (
    text: string,
    handleInternalLinks?: (link: string, index: number) => JSX.Element,
): (string | JSX.Element)[] => {
    return applyUNCFileLinks(
        applyLocalFileLinks(
            applyExternalLinks(applyInternalLinks([text], handleInternalLinks)),
        ),
    )
}

const applyExternalLinks = (
    inputArray: (string | JSX.Element)[],
): (string | JSX.Element)[] => {
    return applyRegexToStringPartsOfArray(
        externalLinksRegex,
        (link: string) => {
            const key = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
            const prefix = link.startsWith("www.") ? "http://" : ""
            const href = `${prefix}${link}`
            return (
                <a
                    key={`external-${key}`}
                    data-ft-link-type="external"
                    data-ft-link={link}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {link}
                </a>
            )
        },
        inputArray,
    )
}

const applyInternalLinks = (
    inputArray: (string | JSX.Element)[],
    handleInternalLinks: (
        link: string,
        index: number,
    ) => JSX.Element = defaultHandleInternalLinks,
): (string | JSX.Element)[] => {
    return applyRegexToStringPartsOfArray(
        internalLinksRegex,
        (link: string) => {
            const key = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
            return handleInternalLinks(link, key)
        },
        inputArray,
    )
}

const applyLocalFileLinks = (
    inputArray: (string | JSX.Element)[],
): (string | JSX.Element)[] => {
    return applyRegexToStringPartsOfArray(
        localFilePathRegex,
        (link: string) => {
            const key = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
            const href = `file:///${link.replace("\\", "/")}`
            return (
                <a
                    key={`file-${key}`}
                    data-ft-link-type="file"
                    data-ft-link={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {link}
                </a>
            )
        },
        inputArray,
    )
}

const applyUNCFileLinks = (
    inputArray: (string | JSX.Element)[],
): (string | JSX.Element)[] => {
    return applyRegexToStringPartsOfArray(
        UNCFilePathRegex,
        (link: string) => {
            const key = Math.floor(Math.random() * Number.MAX_SAFE_INTEGER)
            const href = `file:///${link
                .replace("\\\\?\\UNC\\", "\\\\")
                .replace("\\", "/")}`

            return (
                <a
                    key={`file-${key}`}
                    data-ft-link-type="file"
                    data-ft-link={href}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {link}
                </a>
            )
        },
        inputArray,
    )
}

/*
 * The applyRegexToStringPartsOfArray function takes 3 parameters:
 *   a regex: this will be used to identify the parts of text to be converted
 *   a converterFunction: this function will be applied to each part of the text
 *           that matches the regex provided
 *   an array of string and JSX Elements: we will apply the function to each regex
 *           match in any string in the array
 *
 * An example;
 *   regex: /(a)+/g // matches any sequence of at least 1 'a'
 *   converterFunction: (text) => ( <p> text </p> ) // converts any text into a JSX.Element(<p>text</p>)
 *   array: ["13a", "bbbb", JSX.Element(...), "this is a text with many aaas"] // some array with a lot of a's
 *   this array would be converted in:
 *   array: ["13", JSX.Element(<p>a</p>), "bbbb", JSX.Element(...), "this is ",
 *                 JSX.Element(<p>a</p>), " text with m", JSX.Element(<p>a</p>),
 *                 "ny ", JSX.Element(<p>aaa</p>), "s"]
 */
const applyRegexToStringPartsOfArray = (
    regex: RegExp,
    converterFunctionForRegexMatches: (match: string) => string | JSX.Element,
    inputArray: (string | JSX.Element)[],
): (string | JSX.Element)[] => {
    return inputArray.flatMap((chunk) => {
        if (typeof chunk === "string") {
            const intermediateResultArray: (string | JSX.Element)[] = []
            let lastIndexOfChunkPushedToArray = 0
            let regexMatch: RegExpExecArray | null
            // reset the regex before we start
            regex.lastIndex = 0
            while ((regexMatch = regex.exec(chunk)) !== null) {
                if (lastIndexOfChunkPushedToArray < regexMatch.index) {
                    // push anything between matches as raw strings
                    intermediateResultArray.push(
                        chunk.slice(
                            lastIndexOfChunkPushedToArray,
                            regexMatch.index,
                        ),
                    )
                }
                // let the passed function deal with this type of match
                intermediateResultArray.push(
                    converterFunctionForRegexMatches(regexMatch[0]),
                )
                lastIndexOfChunkPushedToArray = regex.lastIndex
            }
            if (lastIndexOfChunkPushedToArray < chunk.length) {
                // any leftovers at the end are added as a raw string
                intermediateResultArray.push(
                    chunk.slice(lastIndexOfChunkPushedToArray, chunk.length),
                )
            }
            return intermediateResultArray
        } else {
            // not a string, so clearly it was already processed
            return chunk
        }
    })
}

const defaultHandleInternalLinks = (
    link: string,
    index: number,
): JSX.Element => {
    return <p key={`internal-default-${index}`}>{link}</p>
}
